"use client";

import {
  type ComponentType,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";

import { usePathname } from "next/navigation";

import { useLocalStorage } from "@uidotdev/usehooks";

import { EmptyPageIcon } from "@/components/icons-lib";
import { useNavigation } from "@/providers/navigation/hooks/navigation-matcher.hook";
import {
  useGetCustomReportsQuery,
  useGetSavedReportsQuery,
} from "@/providers/store/api/reports/reports";
import type { SystemUserReportTypeEnum } from "@/providers/store/api/reports/system-user-report-type.enum";
import { useAppSelector } from "@/providers/store/hooks";
import { LocalStorageKeys } from "@/shared/enums/local-storage-keys.enum";
import { getNavigationType } from "@/shared/utils/duplicated-tab-recovery.util";

import { resolveRestoredActiveTabId } from "./resolve-restored-active-tab.util";
import {
  type InternalTab,
  type Tab,
  type WorkspaceTabsAction,
  type WorkspaceTabsState,
  TabSource,
} from "./types";

interface WorkspaceTabsContextValue {
  tabs: Tab[];
  activeTabId: string;
  pageTabId: string | null;
  isPageTabActive: boolean;
  openReport: (
    reportId: string,
    label: string,
    showPreviewIndicator?: boolean,
    reportKind?: "saved" | "custom",
    savedSystemUserReportType?: SystemUserReportTypeEnum,
  ) => void;
  closeTab: (tabId: string) => void;
  setActiveTab: (tabId: string) => void;
  updateTab: (
    tabId: string,
    label: string,
    showPreviewIndicator?: boolean,
    icon?: ComponentType<{ className?: string }>,
  ) => void;
  clearAllReports: () => void;
}

const WorkspaceTabsContext = createContext<WorkspaceTabsContextValue | null>(
  null,
);

export function useWorkspaceTabs(): WorkspaceTabsContextValue {
  const ctx = useContext(WorkspaceTabsContext);
  if (!ctx) {
    throw new Error(
      "useWorkspaceTabs must be used within WorkspaceTabsProvider",
    );
  }
  return ctx;
}

/**
 * Get the parent page pathname for a given path
 * Returns the path itself if it's a top-level page, or parent path for nested pages
 */
function getParentPagePathname(pathname: string): string {
  if (!pathname.startsWith("/panel")) {
    return pathname;
  }
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length <= 2) {
    return pathname;
  }
  return `/${segments.slice(0, -1).join("/")}`;
}

const ATTENDANCE_ZONES_WORKSPACE_TAB_PATH = "/panel/data/attendance-zones";
const DISTRICT_BOUNDARY_WORKSPACE_TAB_PATH = "/panel/data/district-boundary";
const DATA_WORKSPACE_TAB_PATH = "/panel/data";
const INTEGRATIONS_PATH = "/panel/data/integrations";

function getSafePathname(pathname: string): string {
  if (pathname.startsWith("/panel/my-district")) {
    return "/panel/my-district";
  }

  if (
    pathname.startsWith(ATTENDANCE_ZONES_WORKSPACE_TAB_PATH) ||
    pathname.startsWith("/panel/reports/preview-map")
  ) {
    return ATTENDANCE_ZONES_WORKSPACE_TAB_PATH;
  }

  if (
    pathname.startsWith(DISTRICT_BOUNDARY_WORKSPACE_TAB_PATH) ||
    pathname.startsWith("/panel/data/district-boundary/preview")
  ) {
    return DISTRICT_BOUNDARY_WORKSPACE_TAB_PATH;
  }

  // Integrations + nested routes (e.g. custom-field-mappings) are not nav entries.
  // Peel to /panel/data so the page tab registers and hard reload still mounts children.
  if (pathname.startsWith(INTEGRATIONS_PATH)) {
    return DATA_WORKSPACE_TAB_PATH;
  }

  const parentPathname = getParentPagePathname(pathname);
  return parentPathname && !parentPathname.includes("undefined")
    ? parentPathname
    : "/panel";
}

function handleSetPageTab(
  state: WorkspaceTabsState,
  action: Extract<WorkspaceTabsAction, { type: "SET_PAGE_TAB" }>,
): WorkspaceTabsState {
  const pageTab: InternalTab = { ...action.tab, source: TabSource.Page };
  const pageTabId = action.tab.id;

  const reportTabs = state.tabs.filter((tab) => tab.source === TabSource.Report);
  const tabs = [pageTab, ...reportTabs];

  if (state.skipNextPageActivation && pageTabId === "/panel/reports") {
    return { ...state, tabs, pageTabId, skipNextPageActivation: false };
  }

  const isDifferentPage = state.pageTabId !== pageTabId;
  const activeTabId = isDifferentPage ? pageTabId : state.activeTabId;

  return {
    ...state,
    tabs,
    activeTabId,
    pageTabId,
    skipNextPageActivation: false,
  };
}

function handleSetActiveTab(
  state: WorkspaceTabsState,
  action: Extract<WorkspaceTabsAction, { type: "SET_ACTIVE_TAB" }>,
): WorkspaceTabsState {
  return {
    ...state,
    activeTabId: action.tabId,
    skipNextPageActivation: false,
  };
}

function buildTabsWithOpenedReport(
  state: WorkspaceTabsState,
  reportTab: InternalTab,
): InternalTab[] {
  const existingIndex = state.tabs.findIndex(
    (tab) => tab.id === reportTab.id && tab.source === TabSource.Report,
  );

  if (existingIndex >= 0) {
    const tabs = [...state.tabs];
    tabs[existingIndex] = reportTab;
    return tabs;
  }

  const pageTab = state.tabs.find((tab) => tab.source === TabSource.Page);
  const otherReportTabs = state.tabs.filter(
    (tab) => tab.source === TabSource.Report && tab.id !== reportTab.id,
  );

  if (pageTab) {
    return [pageTab, reportTab, ...otherReportTabs];
  }

  const nonReportTabs = state.tabs.filter((tab) => tab.source !== TabSource.Report);
  return [reportTab, ...nonReportTabs];
}

function handleOpenReport(
  state: WorkspaceTabsState,
  action: Extract<WorkspaceTabsAction, { type: "OPEN_REPORT" }>,
): WorkspaceTabsState {
  const reportTab: InternalTab = {
    ...action.tab,
    source: TabSource.Report,
  };

  return {
    ...state,
    tabs: buildTabsWithOpenedReport(state, reportTab),
    activeTabId: action.tab.id,
    skipNextPageActivation: true,
  };
}

function handleUpdateReport(
  state: WorkspaceTabsState,
  action: Extract<WorkspaceTabsAction, { type: "UPDATE_REPORT" }>,
): WorkspaceTabsState {
  return {
    ...state,
    tabs: state.tabs.map((tab) =>
      tab.id === action.tabId && tab.source === TabSource.Report
        ? { ...tab, ...action.updates }
        : tab,
    ),
  };
}

function handleCloseReport(
  state: WorkspaceTabsState,
  action: Extract<WorkspaceTabsAction, { type: "CLOSE_REPORT" }>,
): WorkspaceTabsState {
  const tabs = state.tabs.filter(
    (tab) => !(tab.id === action.tabId && tab.source === TabSource.Report),
  );

  const activeTabId =
    state.activeTabId === action.tabId ? state.pageTabId : state.activeTabId;

  return { ...state, tabs, activeTabId: activeTabId ?? null };
}

function handleClearReports(state: WorkspaceTabsState): WorkspaceTabsState {
  const pageTab = state.tabs.find((tab) => tab.source === TabSource.Page);

  return {
    ...state,
    tabs: pageTab ? [pageTab] : [],
    activeTabId: state.pageTabId,
  };
}

/**
 * Reducer for predictable state updates
 */
function reducer(
  state: WorkspaceTabsState,
  action: WorkspaceTabsAction,
): WorkspaceTabsState {
  switch (action.type) {
    case "SET_PAGE_TAB":
      return handleSetPageTab(state, action);
    case "SET_ACTIVE_TAB":
      return handleSetActiveTab(state, action);
    case "OPEN_REPORT":
      return handleOpenReport(state, action);
    case "UPDATE_REPORT":
      return handleUpdateReport(state, action);
    case "CLOSE_REPORT":
      return handleCloseReport(state, action);
    case "CLEAR_REPORTS":
      return handleClearReports(state);

    default:
      return state;
  }
}

/**
 * Simplified workspace tabs provider
 */
export function WorkspaceTabsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const { navigation } = useNavigation();

  const [savedReportTabs, setSavedReportTabs] = useLocalStorage<
    Omit<Tab, "closable">[]
  >(LocalStorageKeys.ReportsOpenTabs, []);

  const [savedActiveTabId, setSavedActiveTabId] = useLocalStorage<string>(
    LocalStorageKeys.ReportsActiveTabId,
    "",
  );

  // Initial state
  const initialState: WorkspaceTabsState = {
    tabs: [],
    activeTabId: null,
    pageTabId: null,
    skipNextPageActivation: false,
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const selectedDistrictId = useAppSelector(
    (storeState) => storeState.district.selectedDistrict?.id ?? null,
  );

  const { data: savedReportsData, isLoading: isSavedReportsLoading } =
    useGetSavedReportsQuery(
      { districtId: selectedDistrictId ?? "" },
      { skip: !selectedDistrictId, refetchOnMountOrArgChange: true },
    );

  const { data: mineCustomReports, isLoading: isMineCustomReportsLoading } =
    useGetCustomReportsQuery(
      { districtId: selectedDistrictId ?? "", scope: "mine" },
      { skip: !selectedDistrictId, refetchOnMountOrArgChange: true },
    );

  const {
    data: districtCustomReports,
    isLoading: isDistrictCustomReportsLoading,
  } = useGetCustomReportsQuery(
    { districtId: selectedDistrictId ?? "", scope: "district" },
    { skip: !selectedDistrictId, refetchOnMountOrArgChange: true },
  );

  const isReportsLoading =
    isSavedReportsLoading ||
    isMineCustomReportsLoading ||
    isDistrictCustomReportsLoading;

  const persistReportTabsToStorageRef = useRef(false);

  // Persist report tabs to localStorage when they change
  useEffect(() => {
    if (!persistReportTabsToStorageRef.current) return;

    const reportTabs = state.tabs
      .filter((t) => t.source === TabSource.Report)
      .map(({ closable: _, ...tab }) => tab);
    setSavedReportTabs(reportTabs);
  }, [state.tabs, setSavedReportTabs]);

  // Persist last active workspace tab (page path or report id)
  useEffect(() => {
    if (!persistReportTabsToStorageRef.current) return;

    const effectiveActiveId = state.activeTabId ?? state.pageTabId ?? pathname;
    if (typeof effectiveActiveId !== "string" || effectiveActiveId === "")
      return;

    setSavedActiveTabId(effectiveActiveId);
  }, [state.activeTabId, state.pageTabId, pathname, setSavedActiveTabId]);

  // Update page tab when pathname changes
  useEffect(() => {
    const safePathname = getSafePathname(pathname);

    // Try to find route info for the page
    const route = navigation.findRouteByHref(safePathname);

    if (route) {
      const Icon = route.meta?.icon ?? EmptyPageIcon;
      dispatch({
        type: "SET_PAGE_TAB",
        tab: {
          id: safePathname,
          label: route.name,
          icon: Icon,
          closable: false,
        },
      });
    }
  }, [pathname, navigation]);

  const savedReportTabsRef = useRef(savedReportTabs);
  savedReportTabsRef.current = savedReportTabs;

  const savedActiveTabIdRef = useRef(savedActiveTabId);
  savedActiveTabIdRef.current = savedActiveTabId;

  const applyRestoredActiveTab = useCallback(
    (savedId: string, safePathname: string, reportIds: string[]) => {
      dispatch({
        type: "SET_ACTIVE_TAB",
        tabId: resolveRestoredActiveTabId({
          savedId,
          safePathname,
          pathname,
          reportIds,
          navigationType: getNavigationType(),
        }),
      });
    },
    [dispatch, pathname],
  );

  // Restore report tabs from useLocalStorage (may hydrate after first paint)
  const hasRestoredTabsRef = useRef(false);
  useEffect(() => {
    if (hasRestoredTabsRef.current) return;

    // Wait for reports to load if there are report tabs to restore
    const hasReportTabsToRestore = savedReportTabs.some((t) => t.reportKind);
    if (hasReportTabsToRestore && (isReportsLoading || !selectedDistrictId)) {
      return;
    }

    if (savedReportTabs.length > 0) {
      const validReportIds = new Set<string>();
      savedReportsData?.reports?.forEach((r) => validReportIds.add(r.id));
      mineCustomReports?.forEach((r) => validReportIds.add(r.id));
      districtCustomReports?.forEach((r) => validReportIds.add(r.id));

      savedReportTabs.forEach((tab) => {
        if (!tab || typeof tab.id !== "string") return;

        // Skip non-existent reports
        if (tab.reportKind && !validReportIds.has(tab.id)) return;

        dispatch({
          type: "OPEN_REPORT",
          tab: { ...tab, closable: true },
        });
      });

      hasRestoredTabsRef.current = true;
      persistReportTabsToStorageRef.current = true;

      const safePathname = getSafePathname(pathname);
      const restoredReportIds = savedReportTabs
        .filter((tab) => {
          if (!tab || typeof tab.id !== "string") return false;
          if (tab.reportKind && !validReportIds.has(tab.id)) return false;
          return true;
        })
        .map((t) => t.id);

      const savedIdNow = savedActiveTabIdRef.current;
      if (savedIdNow) {
        applyRestoredActiveTab(savedIdNow, safePathname, restoredReportIds);
      } else {
        queueMicrotask(() => {
          applyRestoredActiveTab(
            savedActiveTabIdRef.current,
            safePathname,
            restoredReportIds,
          );
        });
      }

      return;
    }

    queueMicrotask(() => {
      if (hasRestoredTabsRef.current) return;
      if (savedReportTabsRef.current.length > 0) return;

      hasRestoredTabsRef.current = true;
      persistReportTabsToStorageRef.current = true;
    });
  }, [
    savedReportTabs,
    pathname,
    applyRestoredActiveTab,
    isReportsLoading,
    selectedDistrictId,
    savedReportsData,
    mineCustomReports,
    districtCustomReports,
  ]);

  /**
   * Open a report in a new tab
   */
  const openReport = useCallback(
    (
      reportId: string,
      label: string,
      showPreviewIndicator?: boolean,
      reportKind?: "saved" | "custom",
      savedSystemUserReportType?: SystemUserReportTypeEnum,
    ) => {
      dispatch({
        type: "OPEN_REPORT",
        tab: {
          id: reportId,
          label,
          closable: true,
          showPreviewIndicator,
          reportKind,
          ...(savedSystemUserReportType !== undefined && { savedSystemUserReportType }),
        },
      });
    },
    [],
  );

  /**
   * Close a tab (only report tabs can be closed)
   */
  const closeTab = useCallback((tabId: string) => {
    dispatch({ type: "CLOSE_REPORT", tabId });
  }, []);

  /**
   * Set the active tab
   */
  const setActiveTab = useCallback((tabId: string) => {
    dispatch({ type: "SET_ACTIVE_TAB", tabId });
  }, []);

  /**
   * Update tab properties (label, preview indicator)
   */
  const updateTab = useCallback(
    (
      tabId: string,
      label: string,
      showPreviewIndicator?: boolean,
      icon?: ComponentType<{ className?: string }>,
    ) => {
      dispatch({
        type: "UPDATE_REPORT",
        tabId,
        updates: {
          label,
          showPreviewIndicator,
          ...(icon !== undefined && { icon }),
        },
      });
    },
    [],
  );

  /**
   * Clear all report tabs
   */
  const clearAllReports = useCallback(() => {
    dispatch({ type: "CLEAR_REPORTS" });
  }, []);

  // Derived values
  const tabs: Tab[] = useMemo(
    () => state.tabs.map(({ source: _, ...tab }) => tab),
    [state.tabs],
  );

  const activeTabId = useMemo(
    () => state.activeTabId ?? state.pageTabId ?? pathname,
    [state.activeTabId, state.pageTabId, pathname],
  );

  const pageTabId = state.pageTabId;

  const isPageTabActive = useMemo(
    () =>
      Boolean(
        activeTabId === pageTabId ||
        (pageTabId && pathname.startsWith(activeTabId)),
      ),
    [activeTabId, pageTabId, pathname],
  );

  const value: WorkspaceTabsContextValue = useMemo(
    () => ({
      tabs,
      activeTabId,
      pageTabId,
      isPageTabActive,
      openReport,
      closeTab,
      setActiveTab,
      updateTab,
      clearAllReports,
    }),
    [
      tabs,
      activeTabId,
      pageTabId,
      isPageTabActive,
      openReport,
      closeTab,
      setActiveTab,
      updateTab,
      clearAllReports,
    ],
  );

  return (
    <WorkspaceTabsContext.Provider value={value}>
      {children}
    </WorkspaceTabsContext.Provider>
  );
}
